import { useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  MessageSquare,
  Plus,
  ChevronRight,
  Trash2,
  MoreVertical,
} from "lucide-react";
import { useConversations, extractTextFromContent } from "@/features/ai-chat";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuAction,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui/sidebar";

export function NavConversations() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isMobile } = useSidebar();
  const { conversations, refreshConversations, removeConversation } =
    useConversations(true);
  const previousPathRef = useRef(location.pathname);

  useEffect(() => {
    const currentPath = location.pathname;
    const previousPath = previousPathRef.current;

    if (
      currentPath.startsWith("/student/home") &&
      currentPath !== previousPath
    ) {
      const timeoutId = setTimeout(() => {
        refreshConversations();
      }, 500);

      previousPathRef.current = currentPath;

      return () => clearTimeout(timeoutId);
    }
  }, [location.pathname, refreshConversations]);

  const getConversationTitle = (conversation) => {
    if (conversation.title) return conversation.title;

    const firstUserMessage = conversation.messages?.find(
      (msg) => msg.role === "user"
    );
    if (firstUserMessage) {
      const text = extractTextFromContent(firstUserMessage.content);
      return text.substring(0, 30) + (text.length > 30 ? "..." : "");
    }

    return "New Conversation";
  };

  const handleNewChat = () => {
    navigate("/student/home");
  };

  const handleDelete = async (e, conversationId) => {
    e.preventDefault();
    e.stopPropagation();

    const success = await removeConversation(conversationId);
    if (success && location.pathname === `/student/home/${conversationId}`) {
      navigate("/student/home");
    }
  };

  return (
    <SidebarGroup className="-mt-4">
      <SidebarMenu>
        <Collapsible asChild defaultOpen={true} className="group/collapsible">
          <SidebarMenuItem>
            <CollapsibleTrigger asChild>
              <SidebarMenuButton tooltip="Chat History">
                <MessageSquare />
                <span>Chat History</span>
                <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
              </SidebarMenuButton>
            </CollapsibleTrigger>
            <SidebarMenuAction onClick={handleNewChat}>
              <Plus />
              <span className="sr-only">New Chat</span>
            </SidebarMenuAction>
            <CollapsibleContent>
              <SidebarMenuSub>
                {conversations.length === 0 ? (
                  <SidebarMenuSubItem>
                    <div className="flex flex-col items-center justify-center py-4 text-center text-sm text-muted-foreground">
                      <MessageSquare className="mb-2 h-6 w-6 opacity-50" />
                      <p className="text-xs">No conversations yet</p>
                    </div>
                  </SidebarMenuSubItem>
                ) : (
                  conversations.map((conversation) => {
                    const conversationId = conversation._id || conversation.id;
                    const isActive =
                      location.pathname === `/student/home/${conversationId}`;

                    return (
                      <SidebarMenuSubItem
                        key={conversationId}
                        className="group relative"
                      >
                        <SidebarMenuSubButton
                          asChild
                          isActive={isActive}
                          className="pr-5"
                        >
                          <Link to={`/student/home/${conversationId}`}>
                            <span>{getConversationTitle(conversation)}</span>
                          </Link>
                        </SidebarMenuSubButton>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <SidebarMenuAction showOnHover>
                              <MoreVertical />
                              <span className="sr-only">More</span>
                            </SidebarMenuAction>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            className="w-48 rounded-lg"
                            side="bottom"
                            align={isMobile ? "end" : "start"}
                          >
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onClick={(e) => handleDelete(e, conversationId)}
                            >
                              <Trash2 />
                              <span>Delete</span>
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </SidebarMenuSubItem>
                    );
                  })
                )}
              </SidebarMenuSub>
            </CollapsibleContent>
          </SidebarMenuItem>
        </Collapsible>
      </SidebarMenu>
    </SidebarGroup>
  );
}
